import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import NvFroala from 'froala/components/nv-froala';
import { FroalaViewMode, ToolbarOptions, UploadType } from 'froala/helpers/nv-froala-constants';
import { SanitizationLevel } from 'froala/helpers/sanitizer';
import React, { useCallback, useEffect, useState } from 'react';
import { closeEditTranslationComponent } from 'redux/actions/edit-translations';
import { translationUpdate } from 'redux/actions/users';
import { TranslationInfo } from 'redux/schemas/models/edit-translations';
import { useAppDispatch } from 'redux/store';
import { white } from 'styles/global_defaults/colors';
import { doubleSpacing } from 'styles/global_defaults/scaffolding';
import { getLectureComponent } from 'redux/selectors/lecture-components';
import { ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';

type EditableProps = {
  editInfo: TranslationInfo,
  containerClass: string,
  onSaveRef: (fn: () => void) => void,
  setSavingDisabled: (boolean) => void,
  withViewOptions?: boolean;
};

const EditableRichText = ({
  editInfo,
  containerClass,
  onSaveRef,
  setSavingDisabled,
  withViewOptions,
}: EditableProps) => {
  const lectureComponent = useSelector((state) => (
    getLectureComponent(state, editInfo.id)
  )) as NLectureComponent<ComponentType.RICH_TEXT>;
  const name = Object.keys(lectureComponent.translationIds)[0];
  const [value, setValue] = useState(lectureComponent[name]);
  const dispatch = useAppDispatch();
  const save = useCallback(() => {
    const { id } = editInfo;
    const payload = {
      id,
      attributes: [
        {
          translationId: lectureComponent.translationIds[name],
          name,
          text: value,
          elementType: editInfo.type,
        },
      ],
    };
    dispatch(translationUpdate(payload)).then(() => {
      dispatch(closeEditTranslationComponent());
    });
  }, [value]);

  useEffect(() => {
    onSaveRef(() => save);
  }, [onSaveRef, save]);

  useEffect(() => {
    if (lectureComponent[name] !== value) {
      setSavingDisabled(false);
    }
    return (() => {
      setSavingDisabled(true);
    });
  }, [value]);

  const simpleTextStyles = css`
    width: 100%;
    margin: 0 ${doubleSpacing}px;
    .bs4-form-control {
      background: ${white};
    }
    .rich-text-froala {
      ${withViewOptions
      && css`
        background: ${editInfo.viewOptions.backgroundColor};
        color: ${editInfo.viewOptions.textColor};
        text-align: ${editInfo.viewOptions.textAlign};
        vertical-align: ${editInfo.viewOptions.verticalAlign};
        // To avoid the white hover when editing
        .fr-box.nv-froala-editor:hover, .nv-froala-editor:not(.no-background):hover .fr-wrapper:not(.show-placeholder) .fr-element {
          background: ${editInfo.viewOptions.backgroundColor};
        }
        .nv-froala-editor .fr-element {
          color: ${editInfo.viewOptions.textColor};
        }
      `}
    }
  `;
  return (
    <div css={simpleTextStyles} className='rich-text'>
      <NvFroala
        className='rich-text-froala'
        preset={FroalaViewMode.NORMAL}
        sanitizationLevel={SanitizationLevel.BASIC}
        toolbarButtons={[
          ToolbarOptions.COLOR,
          ToolbarOptions.BOLD,
          ToolbarOptions.ITALIC,
          ToolbarOptions.UNDERLINE,
          ToolbarOptions.FORMAT_UL,
          ToolbarOptions.FORMAT_OL,
          ToolbarOptions.PARAGRAPH_STYLE,
          ToolbarOptions.ALIGN,
          ToolbarOptions.INSERT_LINK,
        ]}
        uploadType={UploadType.NONE}
        value={value}
        onChange={(newValue) => setValue(newValue)}
        colorPickerContainer={containerClass}
      />
    </div>
  );
};

export default EditableRichText;
